import isValidProp from '@emotion/is-prop-valid';
import * as Sentry from '@sentry/react';
import { MotionConfig } from 'framer-motion';
import { OverlayProvider } from 'react-aria';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router-dom';

import { App } from './App';
import { AppContainer } from './AppContainer';
import { ContextMenuContextProvider } from './components/common/context-menu/ContextMenuContextProvider';
import { GlobalOverlayContextProvider } from './components/overlay/GlobalOverlayContextProvider';
import { NavContextProvider } from './containers/NavContext';
import { history } from './history';
import { LayoutContextProvider } from './LayoutContext';
import { installWebKitPostDragPressWorkaround } from './lib/dom/installWebKitPostDragPressWorkaround';
import { currentOS, OS } from './lib/platform';
import { MobilePage } from './mobile/MobilePage';
import { LocaleProvider } from './providers/LocaleProvider';
import { authStore } from './store/AuthStore';
import { cssClass } from './theme/cssClass';
import { GlobalStyles } from './theme/Global';

if (import.meta.env.MODE === 'production' && import.meta.env.PROD === true) {
  Sentry.init({
    dsn: 'https://57677201cd54491aa453cabe89f30c0b@o4504916257931264.ingest.sentry.io/4504949401387008',
    release: 'homey-web-app@' + import.meta.env.VITE_VERSION,
    ignoreErrors: [],
    beforeSend(event, hint) {
      // return null;
      return event;
    },
    // debug: true,
  });
}

const continueDesktopVersion = localStorage.getItem('continueDesktopVersion') === 'true';

const url = new URL(window.location.href);
const code = url.searchParams.get('code');
const state = url.searchParams.get('state');
const rootElement = document.getElementById('root');

if (rootElement == null) {
  throw new Error('Missing root element');
}

const root = createRoot(rootElement);
installWebKitPostDragPressWorkaround();

if (code && state) {
  async function handleAuthentication() {
    if (code && state) {
      try {
        await authStore.authenticateWithAuthorizationCode(code);
        console.log('Redirecting to', state);
        window.location.href = state;
      } catch (err) {
        window.alert(err);
      }
    }
  }

  handleAuthentication();
} else if ((currentOS === OS.IOS || currentOS === OS.ANDROID) && continueDesktopVersion === false) {
  root.render(
    <OverlayProvider className={cssClass.overlayProvider}>
      {/* <GlobalStyles /> */}
      <MobilePage />
    </OverlayProvider>,
  );
} else {
  root.render(
    <LocaleProvider>
      <AppContainer>
        {/** https://github.com/framer/motion/issues/1406 */}
        <MotionConfig isValidProp={isValidProp}>
          <OverlayProvider className={cssClass.overlayProvider}>
            <GlobalOverlayContextProvider>
              <Router history={history}>
                <DndProvider backend={HTML5Backend}>
                  <ContextMenuContextProvider>
                    <LayoutContextProvider>
                      <NavContextProvider>
                        <GlobalStyles />
                        <App />
                      </NavContextProvider>
                    </LayoutContextProvider>
                  </ContextMenuContextProvider>
                </DndProvider>
              </Router>
            </GlobalOverlayContextProvider>
          </OverlayProvider>
        </MotionConfig>
      </AppContainer>
    </LocaleProvider>,
  );
}
